/**
 * One trend series — a sparkline, or its expanded detail chart
 * (REQ-STATUS-074, REQ-STATUS-079).
 *
 * The swap and the disclosure control live here rather than in each pane,
 * because three panes render trends (component, interface, connection) and a
 * copy per pane is three toggles that drift (CLAUDE.md §4.4).
 *
 * The expanded chart **replaces** its sparkline rather than sitting below it
 * (§9.3): it is a strict superset — same line, plus axes, units and thresholds
 * — so rendering both would put one series on screen twice in one section, and
 * would double the row's height in a section whose stacking pressure is the
 * reason this feature exists.
 */
import type React from "react";
import { Sparkline, type TrendPoint } from "@/system-status/status-detail-primitives";
import { TrendDetailChart } from "@/system-status/trend-detail-chart";
import type { MetricUnit } from "@/system-status/metric-units";
import type { MetricThreshold } from "@/system-status/trend-detail-model";
import type { HistoryWindow } from "@/system-status/use-component-history";

/**
 * The per-series expand/collapse control.
 *
 * A ``<button>``, never a link or a row click: expanding a plot is **not a
 * selection**, and must not change the inspected entity (ADR-024 §5). State is
 * announced through ``aria-expanded`` rather than by the glyph alone, and the
 * accessible name **names the metric** — "Expand", repeated down a stack of ten
 * plots, tells a screen-reader user nothing about which one they are on
 * (REQ-STATUS-070).
 *
 * **28 × 28, above the 24 × 24 WCAG 2.5.8 (Target Size, Minimum) floor.** It
 * shipped as a bare glyph roughly 11 × 16 and the first 2026-08-03 visual pass
 * flagged it as hard to hit; the second found 24 × 24 still reading as a
 * hairline, so the floor is treated as a minimum rather than a target. The
 * glyph scales with the box — 11 px type in a 28 px target looks like a mistake
 * rather than a control.
 */
export function TrendExpandToggle({
  expanded,
  metricName,
  onToggle,
  testId,
}: {
  expanded: boolean;
  metricName: string;
  onToggle: () => void;
  testId: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-expanded={expanded}
      aria-label={`${expanded ? "Collapse" : "Expand"} ${metricName} detail chart`}
      onClick={onToggle}
      className="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-body font-medium leading-none text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
    >
      {/* aria-hidden: the accessible name above carries the meaning, so the
          glyph is decoration and must not be announced twice. */}
      <span aria-hidden="true">{expanded ? "−" : "+"}</span>
    </button>
  );
}

export function TrendSeriesRow({
  points,
  label,
  accessibleName,
  description,
  unit,
  threshold,
  window,
  expanded,
  onToggle,
  testId,
}: {
  points: readonly TrendPoint[];
  label: string;
  accessibleName: string;
  description?: string;
  unit?: MetricUnit;
  threshold?: MetricThreshold;
  window: HistoryWindow;
  expanded: boolean;
  onToggle: () => void;
  testId: string;
}): React.ReactElement {
  const disclosure = (
    <TrendExpandToggle
      expanded={expanded}
      metricName={label}
      onToggle={onToggle}
      testId={`${testId}-expand`}
    />
  );
  if (expanded) {
    return (
      <TrendDetailChart
        points={points}
        label={label}
        accessibleName={accessibleName}
        description={description}
        unit={unit}
        threshold={threshold}
        window={window}
        disclosure={disclosure}
        testId={`${testId}-detail`}
      />
    );
  }
  return (
    <Sparkline
      points={points}
      label={label}
      accessibleName={accessibleName}
      description={description}
      disclosure={disclosure}
      testId={testId}
    />
  );
}
